<template>
	<div class="size-selection-wrapper">
		<breakcrum :items="breadcrumItems"></breakcrum>
		<div class="section-header">
			{{ $t("enterDimensions.header") }}
		</div>
		<div class="section-subheader">
			{{ $t("enterDimensions.subheader") }}
		</div>

		<div class="sizes-wrapper">
			<div class="size">
				<div class="icon image-wrapper">
					<svg
						width="122"
						height="123"
						viewBox="0 0 122 123"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M61 121.53C94.4133 121.53 121.5 94.4361 121.5 61.0148C121.5 27.5935 94.4133 0.5 61 0.5C27.5867 0.5 0.5 27.5935 0.5 61.0148C0.5 94.4361 27.5867 121.53 61 121.53Z"
							stroke="#F4EFFB"
						/>
						<ellipse
							cx="60.9967"
							cy="61.0164"
							rx="52.3404"
							ry="52.3534"
							fill="#F4EFFB"
						/>
						<path
							d="M77.4598 38.8965H44.5313C41.0795 38.8965 38.2812 41.6947 38.2812 45.1465V76.8882C38.2812 80.34 41.0795 83.1382 44.5313 83.1382H77.4598C80.9116 83.1382 83.7098 80.34 83.7098 76.8882V53.4809V47.0138V45.1465C83.7098 41.6947 80.9116 38.8965 77.4598 38.8965Z"
							fill="#DDD2EC"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M70.4146 47.6975H51.5664C49.2192 47.6975 47.3164 49.6003 47.3164 51.9475V70.0864C47.3164 72.4336 49.2192 74.3364 51.5664 74.3364H70.4146C72.7618 74.3364 74.6646 72.4336 74.6646 70.0864V56.5134V52.6488V51.9475C74.6646 49.6003 72.7618 47.6975 70.4146 47.6975Z"
							fill="white"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M70.4185 47.4474H51.5703C49.085 47.4474 47.0703 49.4621 47.0703 51.9474V70.0863C47.0703 72.5716 49.085 74.5863 51.5703 74.5863H70.4185C72.9038 74.5863 74.9185 72.5716 74.9185 70.0863V56.5132V52.6487V51.9474C74.9185 49.4621 72.9038 47.4474 70.4185 47.4474ZM70.4185 47.9474H58.6315V53.5134H58.1315V47.9474H51.5703C49.3612 47.9474 47.5703 49.7382 47.5703 51.9474V56.5919H58.5724V57.0919H47.5703V70.0863C47.5703 72.2954 49.3612 74.0863 51.5703 74.0863H58.9722V68.8845H51.8077V68.3845H58.9722V62.6762C58.9722 61.6043 59.8411 60.7354 60.913 60.7354H65.3646V61.2354H60.913C60.1173 61.2354 59.4722 61.8805 59.4722 62.6762V74.0863H70.4185C72.6276 74.0863 74.4185 72.2954 74.4185 70.0863V61.2354H70.4204V60.7354H74.4185V56.5132V52.6487V51.9474C74.4185 49.7382 72.6276 47.9474 70.4185 47.9474Z"
							fill="#7345B2"
						/>
						<path
							d="M74.9165 43.0679L79.2954 43.0688L79.2954 47.4479"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M75.668 46.6395L79.2127 43.094"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M46.2305 46.6395L42.6858 43.094"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M79.2109 78.3319L75.6662 74.7864"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M47.0718 43.0679L42.6929 43.0688L42.6929 47.4479"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M74.9165 78.4662L79.2954 78.4653V74.0863"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M42.6875 78.3319L46.2322 74.7864"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M47.0718 78.4662L42.6929 78.4653V74.0862"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
					</svg>
				</div>
				<div class="text">{{ $t("enterDimensions.roomLength") }}</div>
				<div class="dimension-indication">
					{{ $t("enterDimensions.dimensionIndicators.feet") }}
				</div>
				<div class="input-wrapper">
					<input
						v-model="length"
						type="number"
						class="strip-input-default-styles"
						:min="minLength"
						:max="maxLength"
						@blur="
							isNumberInValidRange(
								$event,
								minLength,
								maxLength,
								'length'
							)
						"
						@keyup.enter="
							isNumberInValidRange(
								$event,
								minLength,
								maxLength,
								'length'
							)
						"
					/>
				</div>
				<div
					v-show="this.isErrorMessageVisible['length']"
					class="range-error-indicator"
				>
					{{
						$t("enterDimensions.errorMessage", {
							min: minLength,
							max: maxLength,
						})
					}}
				</div>
			</div>
			<div class="size">
				<div class="icon image-wrapper">
					<svg
						width="122"
						height="123"
						viewBox="0 0 122 123"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M61 121.53C94.4133 121.53 121.5 94.4361 121.5 61.0148C121.5 27.5935 94.4133 0.5 61 0.5C27.5867 0.5 0.5 27.5935 0.5 61.0148C0.5 94.4361 27.5867 121.53 61 121.53Z"
							stroke="#F4EFFB"
						/>
						<ellipse
							cx="60.9967"
							cy="61.0164"
							rx="52.3404"
							ry="52.3534"
							fill="#F4EFFB"
						/>
						<rect
							x="85.2461"
							y="67.8885"
							width="14.283"
							height="48.5"
							rx="2.50447"
							transform="rotate(90 85.2461 67.8885)"
							fill="#DDD2EC"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M71.7188 72.0001L71.7188 67.9145"
							stroke="#7345B2"
							stroke-width="0.281808"
						/>
						<path
							d="M62.332 72.0001L62.332 67.9145"
							stroke="#7345B2"
							stroke-width="0.281808"
						/>
						<path
							d="M81.1094 72.0001L81.1094 67.9145"
							stroke="#7345B2"
							stroke-width="0.281808"
						/>
						<path
							d="M76.4141 69.9573L76.4141 67.9145"
							stroke="#7345B2"
							stroke-width="0.281808"
						/>
						<path
							d="M67.0234 69.9573L67.0234 67.9145"
							stroke="#7345B2"
							stroke-width="0.281808"
						/>
						<path
							d="M57.6367 69.9573L57.6367 67.9145"
							stroke="#7345B2"
							stroke-width="0.281808"
						/>
						<path
							d="M48.25 69.9573L48.25 67.9145"
							stroke="#7345B2"
							stroke-width="0.281808"
						/>
						<path
							d="M52.9453 72.0001L52.9453 67.9145"
							stroke="#7345B2"
							stroke-width="0.281808"
						/>
						<path
							d="M43.5547 72.0001L43.5547 67.9145"
							stroke="#7345B2"
							stroke-width="0.281808"
						/>
						<path
							d="M77.1011 43.0647L77.1011 65.5444L44.958 65.5444L44.958 43.0647C44.958 41.4078 46.3012 40.0647 47.958 40.0647L74.1011 40.0647C75.7579 40.0647 77.1011 41.4078 77.1011 43.0647Z"
							fill="white"
						/>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M48.4894 39.5994H73.5052C75.649 39.5994 77.3869 41.3373 77.3869 43.4811V45.6628V50.236V65.5444H76.8869V55.7783H72.1545V55.2783H76.8869V50.236V45.6628V43.4811C76.8869 41.6134 75.3729 40.0994 73.5052 40.0994H58.1545V46.686H57.6545V40.0994H48.4894C46.6217 40.0994 45.1077 41.6134 45.1077 43.4811V50.3751H58.1321V50.8751H45.1077V65.5444H44.6077V43.4811C44.6077 41.3373 46.3456 39.5994 48.4894 39.5994ZM58.648 65.5444H59.148L59.148 57.2191C59.148 56.4234 59.7931 55.7783 60.5888 55.7783H66.1686V55.2783H60.5888C59.5169 55.2783 58.648 56.1472 58.648 57.2191L58.648 64.3298H50.1233V64.8298H58.648V65.5444Z"
							fill="#7345B2"
						/>
					</svg>
				</div>
				<div class="text">{{ $t("enterDimensions.roomWidth") }}</div>
				<div class="dimension-indication">
					{{ $t("enterDimensions.dimensionIndicators.feet") }}
				</div>
				<div class="input-wrapper">
					<input
						v-model="width"
						type="number"
						class="strip-input-default-styles"
						:min="minWidth"
						:max="maxWidth"
						@blur="
							isNumberInValidRange(
								$event,
								minWidth,
								maxWidth,
								'width'
							)
						"
						@keyup.enter="
							isNumberInValidRange(
								$event,
								minWidth,
								maxWidth,
								'width'
							)
						"
					/>
				</div>
				<div
					v-show="this.isErrorMessageVisible['width']"
					class="range-error-indicator"
				>
					{{
						$t("enterDimensions.errorMessage", {
							min: minWidth,
							max: maxWidth,
						})
					}}
				</div>
			</div>
			<div class="size disabled">
				<div class="icon image-wrapper">
					<svg
						width="122"
						height="123"
						viewBox="0 0 122 123"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M61 121.53C94.4133 121.53 121.5 94.4361 121.5 61.0148C121.5 27.5935 94.4133 0.5 61 0.5C27.5867 0.5 0.5 27.5935 0.5 61.0148C0.5 94.4361 27.5867 121.53 61 121.53Z"
							stroke="#F4EFFB"
						/>
						<ellipse
							cx="60.9967"
							cy="61.0164"
							rx="52.3404"
							ry="52.3534"
							fill="#F4EFFB"
						/>
						<path
							d="M44.2573 44.7198H66.0194V76.3377H44.2573C42.0482 76.3377 40.2573 74.5468 40.2573 72.3377V48.7198C40.2573 46.5106 42.0482 44.7198 44.2573 44.7198Z"
							fill="white"
						/>
						<rect
							x="68.353"
							y="36.3623"
							width="14.283"
							height="48.5119"
							rx="2.50447"
							fill="#DDD2EC"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M72.4645 49.8929L68.3789 49.8929"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M72.4645 59.282L68.3789 59.282"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M72.4645 40.5L68.3789 40.5"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M70.4217 45.1965L68.3789 45.1965"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M70.4217 54.5894L68.3789 54.5894"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M70.4217 63.9784L68.3789 63.9784"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M70.4217 73.3675L68.3789 73.3675"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M72.4645 68.671L68.3789 68.671"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							d="M72.4645 78.0639L68.3789 78.0639"
							stroke="#7345B2"
							stroke-width="0.5"
						/>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M66.0195 44.6485H43.8817C41.7379 44.6485 40 46.3864 40 48.5302V72.7065C40 74.8503 41.7379 76.5882 43.8817 76.5882H66.0195V76.0882H54.5403L54.5403 62.2722C54.5403 61.4764 55.1854 60.8313 55.9812 60.8313H61.5609V60.3313H55.9812C54.9093 60.3313 54.0403 61.2003 54.0403 62.2722L54.0403 69.3851H45.5156V69.8851H54.0403V76.0882H43.8817C42.014 76.0882 40.5 74.5742 40.5 72.7065V55.927H53.5244V55.427H40.5V48.5302C40.5 46.6625 42.014 45.1485 43.8817 45.1485H53.0469V51.7368H53.5469V45.1485H66.0195V44.6485Z"
							fill="#7345B2"
						/>
					</svg>
				</div>
				<div class="text">{{ $t("enterDimensions.roomHeight") }}</div>
				<div class="dimension-indication">
					{{ $t("enterDimensions.dimensionIndicators.feet") }}
				</div>
				<div class="input-wrapper">
					<input
						v-model="height"
						type="number"
						class="strip-input-default-styles"
						:min="minHeight"
						:max="maxHeight"
						@blur="
							isNumberInValidRange(
								$event,
								minHeight,
								maxHeight,
								'height'
							)
						"
						@keyup.enter="
							isNumberInValidRange(
								$event,
								minHeight,
								maxHeight,
								'height'
							)
						"
					/>
				</div>
			</div>
		</div>

		<div class="page-footer">
			<div class="footer-item left"></div>
			<div class="footer-item center">
				<div class="footer-button outlined" @click="handleBackClick">
					{{ $t("buttonText.back") }}
				</div>
				<div class="footer-button" @click="handleNextClick">
					{{ $t("buttonText.next") }}
				</div>
			</div>
			<div class="footer-item right">
				<div class="skip-step-button" @click="handleNextClick">
					{{ $t("buttonText.skipStep") }}
					<svg
						data-v-151938fc=""
						width="6"
						height="10"
						viewBox="0 0 6 10"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							data-v-151938fc=""
							d="M1.13456 0.19789L5.58047 4.63061C5.63324 4.68338 5.67053 4.74055 5.69234 4.80211C5.7145 4.86368 5.72559 4.92964 5.72559 5C5.72559 5.07036 5.7145 5.13632 5.69234 5.19789C5.67053 5.25945 5.63324 5.31662 5.58047 5.36939L1.13456 9.8153C1.01143 9.93843 0.857512 10 0.672816 10C0.488119 10 0.329808 9.93404 0.197882 9.80211C0.0659556 9.67019 -7.19485e-06 9.51627 -7.21022e-06 9.34037C-7.2256e-06 9.16447 0.0659556 9.01055 0.197882 8.87863L4.07651 5L0.197881 1.12137C0.0747501 0.998241 0.0131842 0.846614 0.0131842 0.666491C0.0131842 0.486017 0.0791474 0.329815 0.211073 0.19789C0.343 0.0659633 0.496914 4.57105e-07 0.672815 4.41727e-07C0.848716 4.26349e-07 1.00263 0.0659632 1.13456 0.19789Z"
							fill="black"
						></path>
					</svg>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import breakcrum from "@/components/breakcrum.vue";
import { mapGetters, mapMutations } from "vuex";

export default {
	name: "SizeSelection",
	components: {
		breakcrum,
	},
	data: () => ({
		breadcrumItems: [],
		height: 1,
		width: 1,
		length: 1,
		isErrorMessageVisible: {
			height: false,
			width: false,
			length: false,
			heightRef: null,
			widthRef: null,
			lengthRef: null,
		},
	}),
	computed: {
		...mapGetters(["sizes", "selectedSize"]),
		sizeToCheck() {
			if (this.sizes.length == 0) return null;

			return this.sizes[0];
		},
		defaultLength() {
			if (
				!this.sizeToCheck ||
				(this.sizeToCheck && !this.sizeToCheck.defaultSize)
			)
				return 12;
			else return Number(this.sizeToCheck.defaultSize[0]);
		},
		defaultWidth() {
			if (
				!this.sizeToCheck ||
				(this.sizeToCheck && !this.sizeToCheck.defaultSize)
			)
				return 14;
			else return Number(this.sizeToCheck.defaultSize[1]);
		},
		defaultHeight() {
			if (
				!this.sizeToCheck ||
				(this.sizeToCheck && !this.sizeToCheck.defaultSize)
			)
				return undefined;
			else return Number(this.sizeToCheck.defaultSize[2]);
		},
		minLength() {
			if (!this.sizeToCheck) return 0;
			else return Number(this.sizeToCheck.min[0]);
		},
		maxLength() {
			if (!this.sizeToCheck) return 0;
			else return Number(this.sizeToCheck.max[0]);
		},
		minWidth() {
			if (!this.sizeToCheck) return 0;
			else return Number(this.sizeToCheck.min[1]);
		},
		maxWidth() {
			if (!this.sizeToCheck) return 0;
			else return Number(this.sizeToCheck.max[1]);
		},
		minHeight() {
			if (!this.sizeToCheck) return 0;
			else return Number(this.sizeToCheck.min[2]);
		},
		maxHeight() {
			if (!this.sizeToCheck) return 0;
			else return Number(this.sizeToCheck.max[2]);
		},
		stepForSelectedSize() {
			if (!this.sizeToCheck) return 1;
			else return Number(this.sizeToCheck.step);
		},
	},
	created() {
		if (!this.sizes || this.sizes.lengt == 0) {
			this.$router.push("/");
			return;
		}
		this.breadcrumItems = [
			{
				text: this.$t("breadcrums.roomLayout"),
				svg: `
                    <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="17" cy="17" r="16.7273" fill="#DDD2EC"/>
                        <rect x="8.63672" y="8.63633" width="16.7273" height="16.7273" fill="#DDD2EC"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M12.8919 9.48779C10.9187 9.48779 9.31909 11.0874 9.31909 13.0606V21.2204C9.31909 23.1936 10.9187 24.7933 12.8919 24.7933H21.428C23.4012 24.7933 25.0008 23.1936 25.0008 21.2204V14.7737V12.7427C25.0008 10.945 23.5435 9.48779 21.7459 9.48779H12.8919ZM10.8873 13.0606C10.8873 11.9535 11.7848 11.056 12.8919 11.056H15.2638L15.2638 13.1972H16.3092V11.056H21.7459C22.6775 11.056 23.4326 11.8111 23.4326 12.7427V14.7737V16.6016H22.1156V17.6471H23.4326V21.2204C23.4326 22.3276 22.5351 23.2251 21.428 23.2251H16.7505V18.8921C16.7505 18.2045 17.3079 17.6471 17.9955 17.6471H19.4567V16.6016H17.9955C16.7305 16.6016 15.7051 17.6271 15.7051 18.8921V20.6209H12.3307V21.6663L15.7051 21.6663V23.2251H12.8919C11.7848 23.2251 10.8873 22.3276 10.8873 21.2204V15.4695H15.8864V14.424H10.8873V13.0606Z" fill="#7345B2"/>
                    </svg>
                    `,
				active: false,
			},
			{
				text: this.$t("breadcrums.roomDimensions"),
				svg: `
                    <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="16.8239" cy="17" r="16.7273" fill="#7345B2"/>
                        <rect x="8.46021" y="8.63632" width="16.7273" height="16.7273" fill="#7345B2"/>
                        <rect x="9.50562" y="9.54913" width="14.6363" height="14.6363" rx="2.09091" stroke="#F4EFFB" stroke-width="1.56818"/>
                        <path d="M16.824 11.0703L16.824 22.5703" stroke="#F4EFFB" stroke-width="1.04545" stroke-linecap="round"/>
                        <path d="M15.2554 12.1157L16.8236 11.0703L18.3917 12.1157" stroke="#F4EFFB" stroke-width="1.04545" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M15.2554 21.5248L16.8236 22.5703L18.3917 21.5248" stroke="#F4EFFB" stroke-width="1.04545" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M23.0962 16.8203L10.5508 16.8203" stroke="#F4EFFB" stroke-width="1.04545" stroke-linecap="round"/>
                        <path d="M22.0507 15.2521L23.0962 16.8203L22.0507 18.3885" stroke="#F4EFFB" stroke-width="1.04545" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M11.5962 15.2521L10.5508 16.8203L11.5962 18.3885" stroke="#F4EFFB" stroke-width="1.04545" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    `,
				active: true,
			},
		];
	},
	mounted() {
		this.height = this.defaultHeight || this.minHeight;
		this.width = this.defaultWidth || this.minWidth;
		this.length = this.defaultLength || this.minLength;
	},
	methods: {
		...mapMutations(["setSelectedSize"]),
		handleBackClick() {
			this.$router.push("/layout-selection");
		},
		handleNextClick() {
			try {
				let closestSize = [this.length, this.width, this.height];
				closestSize[0] = this.closestNumberInRange(
					this.minLength,
					this.maxLength,
					this.stepForSelectedSize,
					Number(this.length)
				);
				closestSize[1] = this.closestNumberInRange(
					this.minWidth,
					this.maxWidth,
					this.stepForSelectedSize,
					Number(this.width)
				);
				closestSize[2] = this.closestNumberInRange(
					this.minHeight,
					this.maxHeight,
					this.stepForSelectedSize,
					Number(this.height)
				);

				this.setSelectedSize({
					...(this.sizeToCheck && { ...this.sizeToCheck }),
					userInputDimensions: [this.length, this.width, this.height],
					closestSizeToInput: closestSize,
				});
			} catch (e) {
				console.log(e);
			}

			this.$router.push("/render-preview");
		},
		closestNumberInRange(start, end, step, target) {
			if (start > end || step <= 0) {
				return start;
			}

			let closestNumber = start;
			let minDifference = Math.abs(target - start);

			for (let i = start; i <= end; i += step) {
				const difference = Math.abs(target - i);

				if (difference < minDifference) {
					minDifference = difference;
					closestNumber = i;
				}
			}

			return closestNumber;
		},
		isNumberInValidRange(e, min, max, key) {
			const inputValue = Number(this[key]);

			if (isNaN(inputValue)) {
				this[key] = min;
				e.target.value = min;
				this.handleShowErrorMessage(key);
			} else if (inputValue < min) {
				this[key] = min;
				e.target.value = min;
				this.handleShowErrorMessage(key);
			} else if (inputValue > max) {
				this[key] = max;
				e.target.value = max;
				this.handleShowErrorMessage(key);
			}
		},
		handleShowErrorMessage(key) {
			this.isErrorMessageVisible[key] = true;
			clearTimeout(this.isErrorMessageVisible[`${key}Ref`]);
			this.isErrorMessageVisible[`${key}Ref`] = setTimeout(() => {
				this.isErrorMessageVisible[key] = false;
			}, 7500);
		},
	},
	destroyed() {},
};
</script>

<style lang="scss" scoped>
.size-selection-wrapper {
	height: 100%;
	width: 100%;
	padding-top: 1rem;
	// padding of the footer plus the button padding plus the button font plus 39px arbitry
	padding-bottom: calc(1.3125rem + 2.6875rem + 1.5rem + 1.125rem + 3.6875rem);
	position: relative;

	display: flex;
	flex-direction: column;
	align-items: center;

	@include custom-min(768px) {
		padding-top: 2;
		padding-bottom: calc(1.75rem * 2 + 1rem * 2 + 9.9375rem / 2);
	}

	.section-header {
		font-family: $heading-font;
		font-style: normal;
		font-weight: 400;
		font-size: 1.25rem;
		line-height: 140%;
		text-align: center;
		margin-top: 2rem;
		// padding: 0 1rem;
		width: 91%;

		@include custom-min(768px) {
			font-size: 2rem;
			margin-top: 3rem;
		}
	}

	.section-subheader {
		font-style: normal;
		font-weight: 400;
		font-size: 0.875rem;
		line-height: 150%;
		text-align: center;
		margin-top: 0.75rem;
		// padding: 0 1rem;
		width: 91%;

		@include custom-min(768px) {
			font-size: 1.25rem;
			margin-top: 1rem;
		}
	}

	.sizes-wrapper {
		margin-top: 2rem;
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		width: calc(100% - 2.625rem);

		@include custom-min(768px) {
			flex-wrap: nowrap;
			width: 70%;
			margin-top: 3rem;
			justify-content: space-between;
			align-items: flex-start;
		}

		.size {
			display: flex;
			flex-direction: column;
			align-items: center;
			margin-bottom: 2rem;
			flex-grow: 0;
			flex-basis: 40%;
			justify-content: space-between;

			&:nth-child(1) {
				margin-right: 2.375rem;
			}

			&.disabled {
				opacity: 0.5;
				pointer-events: none;
			}

			@include custom-min(768px) {
				&:nth-child(1) {
					margin-right: 0;
				}
				flex-basis: 33%;
				flex-shrink: 0;
			}

			.icon {
				margin-bottom: 1.5rem;

				svg {
					height: 4rem;
					width: 4rem;
					display: block;

					@include custom-min(768px) {
						height: 7.5rem;
						width: 7.5rem;
					}
				}
			}

			.text {
				margin-bottom: 1rem;
				font-style: normal;
				font-weight: 400;
				font-size: 1rem;
				line-height: 140%;
				text-align: center;
				letter-spacing: 0.01em;

				@include custom-min(768px) {
					margin-bottom: 0.5rem;
					font-size: 1.25rem;
				}
			}

			.dimension-indication {
				margin-bottom: 1.0625rem;
				background-color: color(background-light-purple-1);
				padding: 2px 6px;
				border-radius: 20px;

				@include custom-min(768px) {
					margin-bottom: 0.5rem;
					font-size: 0.75rem;
				}
			}

			.input-wrapper {
				width: 7.25rem;

				@include custom-min(768px) {
					width: 11.25rem;
				}

				input[type="number"]::-webkit-inner-spin-button,
				input[type="number"]::-webkit-outer-spin-button {
					-webkit-appearance: none;
					-moz-appearance: none;
					appearance: none;
					margin: 0;
				}

				input {
					width: 100%;
					padding: 6px 1.375rem;
					border: 1px solid #ddd2ec;
					font-weight: 400;
					font-size: 24px;
					line-height: 140%;
					text-align: center;

					@include custom-min(768px) {
						padding: 0.5rem 3.125rem;
						font-size: 1.4375rem;
					}
				}
			}

			.range-error-indicator {
				font-size: 0.75rem;
				color: red;
				margin-top: 0.625rem;
				// max-width: 60%;
				text-align: center;
			}
		}
	}

	.page-footer {
		display: grid;
		grid-template-rows: 1fr 1fr;

		@include custom-min(768px) {
			grid-template-columns: 1fr 1fr 1fr;
			grid-template-rows: unset;
		}

		.footer-item {
			display: flex;
			align-items: center;
		}

		.left {
			display: none;
			@include custom-min(768px) {
				display: block;
			}
		}
		.center {
			justify-content: center;
		}

		.right {
			justify-content: center;

			@include custom-min(768px) {
				justify-content: flex-end;
				padding-right: 4rem;
			}
		}

		.skip-step-button {
			position: relative;
			display: flex;
			align-items: center;
			justify-content: flex-end;
			cursor: pointer;
			font-style: normal;
			font-weight: 600;
			font-size: 0.9375rem;
			line-height: 1.125rem;
			color: var(--neo-secondary-black-1);

			svg {
				margin-left: 0.375rem;
				width: 0.375rem !important;
				height: 0.625rem !important;
			}
		}
		.outlined {
			margin-right: 1.5rem;
		}
	}
}
</style>

<style lang="scss">
.size-selection-wrapper {
	@include custom-min(768px) {
		.breakcrum-wrapper {
			width: 25%;
		}
	}
}
</style>
